/**
 * growthIntel/consent — consent state module.
 *
 * Default behaviour is 'notice_only' (third-party tags load as today). In
 * 'opt_in' mode third-party tags load only after explicit consent, and Google
 * Consent Mode v2 defaults are set to denied before GTM loads. Global Privacy
 * Control (GPC) always forces denial.
 *
 * The consent BANNER is built but DISABLED by default, and its copy is a
 * placeholder marked "requires legal review" — no legal text is published.
 */

export type ConsentMode = 'notice_only' | 'opt_in';
export type ConsentChoice = 'granted' | 'denied' | 'unknown';

/** The opt-in banner is intentionally OFF until legal review is complete. */
export const CONSENT_BANNER_ENABLED = false;

/** Placeholder banner copy — MUST be replaced after legal review. */
export const CONSENT_PLACEHOLDER_COPY =
  'We use analytics to understand how the site is used. [PLACEHOLDER — requires legal review]';

const STORAGE_KEY = 'cendien_consent_v1';
const MODE_KEY = 'cendien_consent_mode';

let cachedMode: ConsentMode = 'notice_only';

/** True when the browser signals Global Privacy Control. */
export function hasGPC(): boolean {
  if (typeof navigator === 'undefined') return false;
  const nav = navigator as unknown as { globalPrivacyControl?: boolean };
  return nav.globalPrivacyControl === true;
}

/** Sets the consent mode (from admin settings gi.consentMode). */
export function setConsentMode(mode: ConsentMode | undefined): void {
  cachedMode = mode === 'opt_in' ? 'opt_in' : 'notice_only';
  try { localStorage.setItem(MODE_KEY, cachedMode); } catch { /* ignore */ }
}

/** Reads the current consent mode (cached, defaults notice_only). */
export function getConsentMode(): ConsentMode {
  try {
    const stored = localStorage.getItem(MODE_KEY);
    if (stored === 'opt_in' || stored === 'notice_only') cachedMode = stored;
  } catch { /* ignore */ }
  return cachedMode;
}

/** Reads the visitor's explicit choice. */
export function getConsentChoice(): ConsentChoice {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === 'granted' || v === 'denied') return v;
  } catch { /* ignore */ }
  return 'unknown';
}

/** Records an explicit consent decision. */
export function setConsentChoice(choice: Exclude<ConsentChoice, 'unknown'>): void {
  try { localStorage.setItem(STORAGE_KEY, choice); } catch { /* ignore */ }
}

/**
 * Whether third-party tags (GTM/GA4/Clarity) may load.
 * - GPC always denies.
 * - notice_only → allowed (current behaviour).
 * - opt_in → allowed only after an explicit 'granted'.
 */
export function thirdPartyAllowed(): boolean {
  if (hasGPC()) return false;
  if (getConsentMode() === 'opt_in') return getConsentChoice() === 'granted';
  return true;
}

/** Whether a first-party anonymous id may be stored. Same rules as tags. */
export function canStoreAnonId(): boolean {
  return thirdPartyAllowed();
}